// First-run spine (docs/09 §4, UI-LAW §4c): the front door when the
// organization has zero agents — a vertical step list rendered in place of
// the team-wide /transcripts empty state. Whitespace + hairlines + type, no
// cards (UI-LAW §1/§2.1); the one sanctioned multi-step empty state. Step 1
// reuses the ONE shared AgentCreateForm (the selector / agents-index create
// flow) in a Popover; steps 2–3 are the visible path with inert CTAs — their
// flows require an agent, and once one exists the spine is replaced by the
// agent-scoped empty state, which owns the ConnectionCheckLauncher (never
// duplicated here); step 4 is a live CTA into /settings/invitations.
// Container (teams read + create mutation + scope) / View (props-pure) split.

import { useRef, useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import {
  useResolvedWorkingTeamId,
  useWorkingScope,
} from "../../lib/workingScope";
import {
  AgentCreateForm,
  Button,
  Divider,
  Heading,
  Inline,
  Popover,
  Stack,
  Text,
  type TeamSelectorItem,
} from "../../ui";

export function FirstRunSpine() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scope = useWorkingScope();
  const resolvedWorkingTeamId = useResolvedWorkingTeamId();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  // ONE list read feeds the create form's team select (agents-index
  // precedent — a spine-scoped key, fetched only while the spine is mounted).
  const teams = useListQuery("/v1/teams", {
    queryKey: queryKeys.teams.list({ surface: "first-run-spine" }),
  });

  const createAgent = useMutation({
    mutationFn: async (input: { displayName: string; teamId: string }) => {
      const { data, error } = await apiClient.POST("/v1/agents", {
        body: { display_name: input.displayName, team_id: input.teamId },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (created) => {
      setIsCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      // The selector's one-switch semantic (docs/09 §4): the new agent
      // becomes the working scope in place — Transcripts re-scopes and the
      // agent-scoped empty state (connection-check launcher) takes over.
      scope.selectAgent(created.id, created.team_id);
    },
  });

  return (
    <FirstRunSpineView
      createError={createAgent.isError ? createAgent.error.message : null}
      isCreateOpen={isCreateOpen}
      isCreating={createAgent.isPending}
      isTeamsError={teams.errorMessage !== undefined}
      isTeamsPending={teams.isPending}
      teams={teams.items.map((team) => ({
        id: team.id,
        label: team.display_name,
      }))}
      workingTeamId={resolvedWorkingTeamId}
      onCreateAgent={(input) => {
        createAgent.mutate(input);
      }}
      onCreateOpenChange={(open) => {
        if (open) {
          createAgent.reset();
        }
        setIsCreateOpen(open);
      }}
      onInviteTeam={() => {
        // Preserve the working-team scope (docs/09 §4) — team only; the spine
        // exists precisely because no agent does.
        void navigate({
          search:
            resolvedWorkingTeamId === undefined
              ? {}
              : { team: resolvedWorkingTeamId },
          to: "/settings/invitations",
        });
      }}
    />
  );
}

export interface FirstRunSpineViewProps {
  createError: string | null;
  isCreateOpen: boolean;
  isCreating: boolean;
  isTeamsError: boolean;
  isTeamsPending: boolean;
  onCreateAgent: (input: { displayName: string; teamId: string }) => void;
  onCreateOpenChange: (open: boolean) => void;
  onInviteTeam: () => void;
  teams: readonly TeamSelectorItem[];
  workingTeamId?: string;
}

export function FirstRunSpineView({
  createError,
  isCreateOpen,
  isCreating,
  isTeamsError,
  isTeamsPending,
  onCreateAgent,
  onCreateOpenChange,
  onInviteTeam,
  teams,
  workingTeamId,
}: FirstRunSpineViewProps) {
  const createAnchorRef = useRef<HTMLButtonElement>(null);

  return (
    <Stack gap={5}>
      <Heading level={3}>Set up Marker</Heading>
      <Divider />
      <Stack gap={2}>
        <Heading level={3}>1. Create your first agent</Heading>
        <div className="marker-popover-anchor">
          <Button
            ref={createAnchorRef}
            aria-expanded={isCreateOpen}
            aria-haspopup="dialog"
            size="sm"
            type="button"
            onClick={() => {
              onCreateOpenChange(!isCreateOpen);
            }}
          >
            + Agent
          </Button>
          <Popover
            anchorRef={createAnchorRef}
            aria-label="Create agent"
            isOpen={isCreateOpen}
            onDismiss={() => {
              onCreateOpenChange(false);
            }}
          >
            <AgentCreateForm
              createError={createError}
              defaultTeamId={workingTeamId}
              isCreating={isCreating}
              isTeamsError={isTeamsError}
              isTeamsPending={isTeamsPending}
              teams={teams}
              onCancel={() => {
                onCreateOpenChange(false);
              }}
              onCreateAgent={onCreateAgent}
            />
          </Popover>
        </div>
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Heading level={3}>2. Configure its interface</Heading>
        <Inline gap={2}>
          <Button disabled size="sm" type="button" variant="secondary">
            Open agent hub
          </Button>
        </Inline>
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Heading level={3}>3. Run a connection check</Heading>
        <Text variant="muted">
          Prove the wiring with a one-minute simulated conversation.
        </Text>
        <Inline gap={2}>
          <Button disabled size="sm" type="button" variant="secondary">
            Run connection check
          </Button>
        </Inline>
      </Stack>
      <Divider />
      <Stack gap={2}>
        <Heading level={3}>4. Invite your team</Heading>
        <Inline gap={2}>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={onInviteTeam}
          >
            Invite your team
          </Button>
        </Inline>
      </Stack>
    </Stack>
  );
}
